import * as THREE from 'three'

export interface MeasurePoint {
  position: THREE.Vector3
  marker: THREE.Mesh
  invScale: number
}

export interface MeasureSegment {
  start: MeasurePoint
  end: MeasurePoint
  distance: number // in real-world mm
  line: THREE.Line
  label: THREE.Sprite
}

const sphereGeo = new THREE.SphereGeometry(0.05, 12, 12)
const sphereMat = new THREE.MeshBasicMaterial({ color: 0xff3333 })
const lineMat = new THREE.LineBasicMaterial({ color: 0xff3333 })

export class MeasurementTool {
  points: MeasurePoint[] = []
  segments: MeasureSegment[] = []
  private scene: THREE.Scene
  private camera: THREE.Camera
  active = false

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene
    this.camera = camera
  }

  addPoint(worldPosition: THREE.Vector3, invScale = 1): MeasurePoint {
    const marker = new THREE.Mesh(sphereGeo, sphereMat.clone())
    marker.position.copy(worldPosition)
    this.scene.add(marker)
    const pt: MeasurePoint = { position: worldPosition.clone(), marker, invScale }

    if (this.points.length >= 1) {
      const prev = this.points[this.points.length - 1]
      const avgInv = (prev.invScale + pt.invScale) / 2
      const sceneDist = prev.position.distanceTo(pt.position)
      const realDist = sceneDist * avgInv
      const geo = new THREE.BufferGeometry().setFromPoints([prev.position, pt.position])
      const line = new THREE.Line(geo, lineMat.clone())
      this.scene.add(line)

      const mid = new THREE.Vector3().addVectors(prev.position, pt.position).multiplyScalar(0.5)
      const label = this.createLabel(formatDist(realDist))
      label.position.copy(mid)
      this.scene.add(label)

      this.segments.push({ start: prev, end: pt, distance: realDist, line, label })
    }

    this.points.push(pt)
    return pt
  }

  private createLabel(text: string): THREE.Sprite {
    const W = 512, H = 128
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#ff3333'
    ctx.font = 'bold 52px Consolas, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, W / 2, H / 2)
    const tex = new THREE.CanvasTexture(canvas)
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, sizeAttenuation: true, transparent: true })
    const sprite = new THREE.Sprite(mat)
    sprite.scale.set(1.0, 0.25, 1)
    return sprite
  }

  clear() {
    for (const p of this.points) { this.scene.remove(p.marker) }
    for (const s of this.segments) {
      this.scene.remove(s.line)
      this.scene.remove(s.label)
    }
    this.points = []
    this.segments = []
  }

  dispose() {
    this.clear()
  }
}

export function formatDist(mm: number): string {
  if (mm >= 1000) return `${(mm / 1000).toFixed(1)} m`
  if (mm >= 10) return `${(mm / 10).toFixed(1)} cm`
  return `${mm.toFixed(1)} mm`
}
